/**
 * 资产素材库管理器
 * 负责管理资产素材库的业务操作
 * @author Joey
 */

import { useToast } from "@/hooks/use-toast";
import { canUseWindow } from "@/utils/canuseWindow";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { jsonStore } from "@/manager/cache/localdb";
import { knowledgeListFolders, KnowledgeFolderData } from "@/services/knowledge-list-folders";
import { knowledgeCreateFolder } from "@/services/knowledge-create-folder";
import { knowledgeRenameFolder } from "@/services/knowledge-rename-folder";
import { knowledgeDeleteFolder } from "@/services/knowledge-delete-folder";
import { useTranslation } from "react-i18next";
import { knowledgeGetFolderItems } from "@/services/knowledge-get-folder-items";
import { knowledgeBatchDownload } from "@/services/knowledge-batch-download";
import { agentManager } from "./agent-manager";

// 缓存数据结构
interface CacheData {
  knowledge: KnowledgeFolderData[];
  page: number;
  pageSize: number;
  total: number;
}

interface KnowledgeFolderSearchForm {
  page: number;
  pageSize: number;
  total: number;
}

type AssetsKnowledgeChangeEventDetail = {
  knowledgeFolders: KnowledgeFolderData[];
};
type AssetsKnowledgeChangeEvent = CustomEvent<AssetsKnowledgeChangeEventDetail>;

type KnowledgeFolderSearchFormChangeEventDetail = {
  knowledgeFolderSearchForm: KnowledgeFolderSearchForm;
}
type KnowledgeFolderSearchFormChangeEvent = CustomEvent<KnowledgeFolderSearchFormChangeEventDetail>;

class AssetsKnowledgeLocal {
  constructor(private storageKey: string) { }
  async get(): Promise<CacheData | null> {
    return await jsonStore.get<CacheData>(this.storageKey);
  }
  async set(data: CacheData): Promise<void> {
    await jsonStore.set(this.storageKey, data);
  }
  async clear(): Promise<void> {
    await jsonStore.set(this.storageKey, null);
  }
}

class AssetsKnowledgeRemote {
  constructor(private cache: AssetsKnowledgeLocal) { }
  async getCached() {
    return await this.cache.get();
  }
  async fetchAssetsKnowledge(page: number, pageSize: number, userId?: string) {
    const data = await knowledgeListFolders({ page, pageSize, userId });
    return { list: data.list, page: data.page, pageSize: data.size, total: data.total };
  }
  async fetchKnowledgeItems(folderId: number) {
    const data = await knowledgeGetFolderItems(folderId);
    return data.items || [];
  }
  async createFolder(folderName: string) {
    return knowledgeCreateFolder({ parentFolderId: 0, folderName });
  }
  async renameFolder(folderId: number, folderName: string) {
    return knowledgeRenameFolder({ folderId, folderName });
  }
  async deleteFolder(ids: number[]) {
    return knowledgeDeleteFolder(ids);
  }
}

export class AssetsKnowledgeManager {
  private static instance: AssetsKnowledgeManager;
  private readonly STORAGE_KEY = "MIUTA_ASSETS_KNOWLEDGE";
  private activeStorageKey = "";
  private knowledgeFolders: KnowledgeFolderData[] = [];
  private cache: AssetsKnowledgeLocal;
  private api: AssetsKnowledgeRemote;
  private knowledgeFolderSearchForm: KnowledgeFolderSearchForm = {
    page: 1,
    pageSize: 100,
    total: 0,
  }

  private constructor() {
    this.cache = new AssetsKnowledgeLocal(this.getStorageKey());
    this.api = new AssetsKnowledgeRemote(this.cache);
    this.activeStorageKey = this.getStorageKey();
  }

  public static getInstance(): AssetsKnowledgeManager {
    if (!AssetsKnowledgeManager.instance) {
      AssetsKnowledgeManager.instance = new AssetsKnowledgeManager();
    }
    return AssetsKnowledgeManager.instance;
  }

  private getStorageKey() {
    const agentId = agentManager.getAgentId() || "";
    return `${this.STORAGE_KEY}:${agentId}`;
  }

  private getKnowledgeListChangeEventName() {
    return `${this.STORAGE_KEY}:list-change`;
  }

  private getKnowledgeFormChangeEventName() {
    return `${this.STORAGE_KEY}:knowledge-form-change`;
  }

  private ensureStorageScope() {
    const nextStorageKey = this.getStorageKey();
    if (nextStorageKey === this.activeStorageKey) {
      return;
    }
    this.activeStorageKey = nextStorageKey;
    this.cache = new AssetsKnowledgeLocal(nextStorageKey);
    this.api = new AssetsKnowledgeRemote(this.cache);
  }

  // 初始化
  public async init() {
    this.ensureStorageScope();
    try {
      const cached = await this.api.getCached();
      if (cached && cached.knowledge && cached.knowledge.length) {
        this.setKnowledgeFolders(cached.knowledge);
        this.setKnowledgeFolderSearchForm({
          page: cached.page || this.knowledgeFolderSearchForm.page,
          pageSize: cached.pageSize || this.knowledgeFolderSearchForm.pageSize,
          total: cached.total || 0,
        });
        this.fetchAssetsKnowledge();
        return;
      }
    } catch (error) {
      console.error("get cache data failed", error);
      this.setKnowledgeFolders([]);
    }
    await this.fetchAssetsKnowledge();
  }

  // 请求知识库数据
  public async fetchAssetsKnowledge(page?: number, userId?: string) {
    this.ensureStorageScope();
    try {
      const data = await this.api.fetchAssetsKnowledge(
        page || this.knowledgeFolderSearchForm.page,
        this.knowledgeFolderSearchForm.pageSize,
        userId
      );
      this.setKnowledgeFolders(data.list);
      this.setKnowledgeFolderSearchForm({
        page: data.page,
        pageSize: data.pageSize,
        total: data.total
      });
    } catch (error) {
      console.error("Failed to fetch assets knowledge list:", error);
      this.dispatchAssetsKnowledgeChange();
      throw error;
    }
  }

  // 请求知识库目录内文件数据
  public async fetchKnowledgeItems(folderId: number) {
    this.ensureStorageScope();
    try {
      const items = await this.api.fetchKnowledgeItems(folderId);
      return items;
    } catch (error) {
      console.error("Failed to fetch assets knowledge item list:", error);
      throw error;
    }
  }

  // 下载文件
  public async downloadKnowledgeItem(ids: number[], fileName?: string) {
    try {
      await knowledgeBatchDownload(ids, fileName);
    } catch (error) {
      console.error("Failed to download assets knowledge item:", error);
      throw error;
    }
  }

  public async knowledgeCreate(folderName: string) {
    this.ensureStorageScope();
    try {
      const res = await this.api.createFolder(folderName);
      const knowledge = [res.folder, ...this.knowledgeFolders];
      this.setKnowledgeFolders(knowledge);
      return res;
    } catch (error) {
      console.error("Failed to create assets knowledge folder:", error);
      this.dispatchAssetsKnowledgeChange();
      throw error;
    }
  }

  // 重命名知识库
  public async knowledgeRename({ id, folderName }: { id?: number, folderName: string }) {
    this.ensureStorageScope();
    const originKnowledge = [...this.knowledgeFolders];
    try {
      const knowledge = this.knowledgeFolders.map((item) => {
        if (item.id === id) {
          return { ...item, folderName };
        }
        return item;
      });
      this.setKnowledgeFolders(knowledge);
      const res = await this.api.renameFolder(id, folderName);
      return res;
    } catch (error) {
      this.setKnowledgeFolders(originKnowledge);
      throw error;
    }
  }

  // 删除知识库
  public async knowledgeDelete(ids: number[]) {
    this.ensureStorageScope();
    const originKnowledge = [...this.knowledgeFolders];
    try {
      // 乐观更新
      const knowledge = this.knowledgeFolders.filter((item) => !ids.includes(item.id));
      this.setKnowledgeFolders(knowledge);

      await this.api.deleteFolder(ids);
    } catch (error) {
      this.setKnowledgeFolders(originKnowledge);
      throw error;
    }
  }

  // ==================== 读取写入操作 ====================
  public getKnowledgeFolders() {
    return this.knowledgeFolders;
  }
  public setKnowledgeFolders(list: KnowledgeFolderData[]) {
    this.ensureStorageScope();
    this.knowledgeFolders = list;
    this.dispatchAssetsKnowledgeChange();
    this.cache.set({
      knowledge: this.knowledgeFolders,
      page: this.knowledgeFolderSearchForm.page,
      pageSize: this.knowledgeFolderSearchForm.pageSize,
      total: this.knowledgeFolderSearchForm.total,
    });
  }

  public getKnowledgeFolderSearchForm() {
    return this.knowledgeFolderSearchForm;
  }
  public setKnowledgeFolderSearchForm(form: KnowledgeFolderSearchForm) {
    this.ensureStorageScope();
    this.knowledgeFolderSearchForm = form;
    this.dispatchKnowledgeFolderSearchFormChange();
  }

  // ==================== 订阅通知操作 ====================
  // 监听列表变化事件
  private dispatchAssetsKnowledgeChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<AssetsKnowledgeChangeEventDetail>(this.getKnowledgeListChangeEventName(), {
      detail: { knowledgeFolders: this.knowledgeFolders }
    });
    window.dispatchEvent(event);
  }
  public onKnowledgeFoldersChange(listener: (e: AssetsKnowledgeChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = this.getKnowledgeListChangeEventName();
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听请求表单变化事件
  private dispatchKnowledgeFolderSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<KnowledgeFolderSearchFormChangeEventDetail>(this.getKnowledgeFormChangeEventName(), {
      detail: { knowledgeFolderSearchForm: this.knowledgeFolderSearchForm }
    });
    window.dispatchEvent(event);
  }
  public onKnowledgeFolderSearchFormChange(listener: (e: KnowledgeFolderSearchFormChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = this.getKnowledgeFormChangeEventName();
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }
}

// ============= Hooks =============
const useAssetsKnowledgeData = () => {
  const toast = useToast();

  const [knowledgeFolders, setKnowledgeFolders] = useState(assetsKnowledgeManager.getKnowledgeFolders());
  const [loading, setLoading] = useState(false);

  const init = async () => {
    try {
      setLoading(true);
      await assetsKnowledgeManager.init();
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (assetsKnowledgeManager.getKnowledgeFolders().length === 0) {
      init();
    }
  }, []);

  useEffect(() => {
    const unsubscribe = assetsKnowledgeManager.onKnowledgeFoldersChange((e) => {
      setKnowledgeFolders(e.detail.knowledgeFolders)
    })
    return unsubscribe
  }, [])

  return {
    knowledgeFolders,
    loading,
  }
}

// 添加知识库
export const useKnowledgeAdd = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const createKnowledge = async ({ name }: { name: string }) => {
    try {
      setLoading(true);
      const res = await assetsKnowledgeManager.knowledgeCreate(name);
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    createKnowledge,
    loading,
  }
}

// 重命名
export const useKnowledgeRename = () => {
  const { showToastByCode } = useToast();
  const params = useParams();

  const renameKnowledge = async ({ id, name }: { id?: number, name: string }) => {
    try {
      const res = await assetsKnowledgeManager.knowledgeRename({
        id: id || Number(params.id),
        folderName: name
      });
    } catch (error) {
      showToastByCode(error);
      throw new Error(error)
    }
  };

  return {
    renameKnowledge,
  };
};

// 删除知识库
export const useKnowledgeDelete = () => {
  const { t } = useTranslation();
  const { toast, showToastByCode } = useToast();
  const [loading, setLoading] = useState(false);

  const deleteKnowledge = async (id: number | number[]) => {
    try {
      setLoading(true);
      id = Array.isArray(id) ? id : [id];
      await assetsKnowledgeManager.knowledgeDelete(id);
      // 刷新素材目录列表
      // await assetsKnowledgeManager.fetchAssetsKnowledge();
    } catch (error) {
      const errorMsg = error.message;
      const match = errorMsg.match(/ID\s+(\d+)\s+or/);
      if (match) {
        const usedFolderId = Number(match[1]);
        const usedFolder = assetsKnowledgeManager.getKnowledgeFolders().find(f => f.id === usedFolderId);
        if (usedFolder) {
          toast.default({
            description: t('assets.knowledge.delete_used_folder', { folderName: usedFolder.folderName }),
          });
        }
      } else {
        showToastByCode(error);
      }
      throw new Error(error)
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteKnowledge,
    loading,
  };
};


// 下载知识库目录文件
export const useDownloadKnowledgeFolder = () => {
  const { showToastByCode } = useToast();
  const [loading, setLoading] = useState(false);

  const downloadKnowledgeFolder = async (ids: number[]) => {
    try {
      setLoading(true);

      // 获取所有目录信息
      const folders = assetsKnowledgeManager.getKnowledgeFolders();

      const finalItemIds: number[] = [];

      for (const id of ids) {
        const folder = folders.find(f => f.id === id);
        if (!folder) continue;

        // 拉取该目录下的文件
        const items = await assetsKnowledgeManager.fetchKnowledgeItems(id);

        const itemIds = items.map(item => item.id);
        finalItemIds.push(...itemIds);
      }

      await assetsKnowledgeManager.downloadKnowledgeItem(finalItemIds);

    } catch (error) {
      showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    downloadKnowledgeFolder,
    loading,
  };
};

export const assetsKnowledgeHooks = {
  useAssetsKnowledgeData,
  useKnowledgeAdd,
  useKnowledgeRename,
  useKnowledgeDelete,
  useDownloadKnowledgeFolder,
}

// 导出单例实例
export const assetsKnowledgeManager = AssetsKnowledgeManager.getInstance();
